export const SALE_STATUSES = [
  "PAID",
  "PROCESSING",
  "PAYMENT_INITIATED",
  "QUOTATION_GENERATED",
  "CANCELLED",
];

export const TICKET_STATUSES = ["CREATED", "LOCKED", "PAID"];

const startOfDay = (date) => {
  const value = new Date(date);
  value.setHours(0, 0, 0, 0);
  return value;
};

const endOfDay = (date) => {
  const value = new Date(date);
  value.setHours(23, 59, 59, 999);
  return value;
};

export function dateRangeForPreset(preset, now = new Date()) {
  const today = startOfDay(now);
  if (preset === "yesterday") {
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    return { start: yesterday, end: endOfDay(yesterday) };
  }
  if (preset === "month") {
    return {
      start: new Date(now.getFullYear(), now.getMonth(), 1),
      end: endOfDay(now),
    };
  }
  return { start: today, end: endOfDay(now) };
}

export function hasStorePermission(store, permission) {
  if (store?.isOwner) return true;
  const permissions = (store?.permissions || []).map((value) =>
    String(value || "").trim().toUpperCase(),
  );
  return permissions.includes(permission);
}

export function canCreateOrAddTicket(store) {
  return [
    "CAN_WRITE_TICKET_CARTS",
    "CAN_WRITE_TICKET_ITEMS",
    "CAN_ADD_TICKET_ITEMS",
  ].every((permission) => hasStorePermission(store, permission));
}

export function firstPermittedOrdersRoute(store) {
  if (hasStorePermission(store, "CAN_READ_SALE_CARTS")) return "orders_transactions";
  if (hasStorePermission(store, "CAN_READ_TICKET_CARTS")) return "orders_tickets";
  if (hasStorePermission(store, "CAN_READ_ORDERS")) return "orders_incoming_orders";
  return "orders_incoming_orders";
}

export function isEbmStore(store) {
  return store?.ebm_activated === true;
}

export function buildOrderQuery(mode, state, ebmEnabled = false) {
  const params = {
    page: Number(state.page) || 1,
    per_page: Number(state.perPage) || 20,
    "created_at[gt]": new Date(state.start).toISOString(),
    "created_at[lt]": new Date(state.end).toISOString(),
  };
  const copy = (target, value) => {
    if (value !== "" && value !== null && value !== undefined) params[target] = value;
  };
  copy("recorded_by_id[eq]", state.userId);
  copy("payment_account_id[eq]", state.paymentAccountId);
  copy("note[like]", state.note?.trim());
  copy("operational_unit_id", state.operationalUnitId);

  if (mode === "transactions") {
    copy(ebmEnabled ? "invoice_number[eq]" : "local_sale_cart_id[eq]", state.reference?.trim());
    copy("receipt_label[eq]", state.receiptLabel);
    if ((state.status || "PAID") === "PAID") params.include_processing = "true";
  } else {
    params.ticket_kind = "POS";
    copy("local_ticket_cart_id[eq]", state.reference?.trim());
    copy("status[eq]", state.status);
  }
  return params;
}

export function normalizePagination(payload, mode) {
  const body = payload?.data && !Array.isArray(payload.data) ? payload.data : payload;
  const rawItems = body?.items || body?.results || (Array.isArray(body) ? body : []);
  const items = rawItems.map((item) => {
    if (mode === "transactions" && item?.original_sale_cart) {
      return {
        ...item.original_sale_cart,
        invoice: item.invoice,
        invoice_number:
          item.invoice?.invoice_number ||
          item.invoice?.invcNo ||
          item.original_sale_cart.invoice_number,
        receipt_label:
          item.invoice?.receipt_label ||
          item.invoice?.receipt_type ||
          item.original_sale_cart.receipt_label,
      };
    }
    return item;
  });
  const page = Number(body?.page || 1);
  const perPage = Number(body?.per_page || body?.perPage || 20);
  const total = Number(body?.total ?? items.length);
  const pages = Number(body?.pages || Math.max(1, Math.ceil(total / perPage)));
  return {
    items,
    page,
    perPage,
    total,
    pages,
    hasNext: body?.has_next ?? page < pages,
    hasPrev: body?.has_prev ?? page > 1,
  };
}

export function routeQueryFromState(state) {
  const query = {};
  for (const key of [
    "preset",
    "status",
    "userId",
    "paymentAccountId",
    "note",
    "reference",
    "receiptLabel",
    "operationalUnitId",
    "page",
    "perPage",
  ]) {
    if (state[key] !== "" && state[key] !== null && state[key] !== undefined) {
      query[key] = String(state[key]);
    }
  }
  if (state.preset === "custom") {
    query.start = new Date(state.start).toISOString();
    query.end = new Date(state.end).toISOString();
  }
  return query;
}

export function stateFromRoute(query, mode, ebmEnabled, now = new Date()) {
  const preset = ["today", "yesterday", "month", "custom"].includes(query.preset)
    ? query.preset
    : "today";
  const fallback = dateRangeForPreset(preset, now);
  const validStatuses =
    mode === "tickets"
      ? TICKET_STATUSES
      : ebmEnabled
        ? ["EBM_INVOICE", ...SALE_STATUSES]
        : SALE_STATUSES;
  const defaultStatus =
    mode === "transactions" ? (ebmEnabled ? "EBM_INVOICE" : "PAID") : "CREATED";
  const parsed = {
    preset,
    start: query.start && !Number.isNaN(Date.parse(query.start)) ? new Date(query.start) : fallback.start,
    end: query.end && !Number.isNaN(Date.parse(query.end)) ? new Date(query.end) : fallback.end,
    status: validStatuses.includes(query.status) ? query.status : defaultStatus,
    userId: query.userId || "",
    paymentAccountId: query.paymentAccountId || "",
    note: query.note || "",
    reference: query.reference || "",
    receiptLabel: query.receiptLabel || "",
    operationalUnitId: query.operationalUnitId || "",
    page: Math.max(1, Number(query.page) || 1),
    perPage: [10, 20, 50, 100].includes(Number(query.perPage)) ? Number(query.perPage) : 20,
  };
  return parsed;
}

export function recordReference(record, mode) {
  if (mode === "tickets") return record.local_ticket_cart_id || record.id;
  return record.invoice_number || record.local_sale_cart_id || record.id;
}

export function recordAmount(record) {
  return Number(
    record.total_amount ??
      record.amount ??
      record.payment_plan?.total_amount_after_discount ??
      record.payment_plan?.total_amount ??
      0,
  );
}
